import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router';
import type { MeetingSummary } from '@contracts/meetings';
import {
  useKnowledge,
  useLedgerActions,
  useMeetings,
  useOpen,
  useUpcoming,
  useNames,
} from '../api/queries';
import { Broken, Button, Empty, Grade, SkeletonRows, useNotices } from '../ui';
import { ago, clock, dateLabel, dayLong, isToday, until, dayKey } from '../lib/time';
import { firstName } from '../lib/note';
import { asLeft, useActedHere } from '../ledger/acted-here';
import { whyNothing } from '../meeting/why-nothing';

/**
 * Board 02 — three equal thirds: notes, the ledger, what we know. No column
 * taller than the others at rest, each ending in one link to its full page.
 */
export function Today(): ReactElement {
  const meetings = useMeetings();
  const open = useOpen();
  const upcoming = useUpcoming(1);
  const names = useNames();
  const todayRows = (upcoming.data ?? []).filter((e) => isToday(e.startsAt));
  const live = todayRows.find(
    (e) => e.capture === 'RECORDING' || e.capture === 'JOINING' || e.capture === 'WAITING_ROOM',
  );
  const next = (upcoming.data ?? []).find(
    (e) => Date.parse(e.startsAt) > Date.now() && (e.plan === 'WILL_RECORD' || e.capture !== null),
  );
  const recent = (meetings.data ?? []).slice(0, 3);
  // What was closed here stays in its slot, crossed out, until the window
  // loses focus (2026-09-25 — `ledger/acted-here`).
  const acted = useActedHere();
  const items = asLeft(open.data?.items ?? [], open.data?.closedThisWeek ?? [], acted).open;
  const mine = items.filter((i) => i.direction === 'I_OWE');
  const theirs = items.filter((i) => i.direction === 'OWED_TO_ME' && i.ownerEmail);
  const shown = [...mine.slice(0, 2), ...theirs.slice(0, 1)];

  return (
    <div className="page stack-lg">
      <div className="page-head">
        <div className="stack" style={{ gap: 4 }}>
          <h1 className="t-title">{dayLong(new Date().toISOString())}</h1>
          <p className="mono">
            {todayRows.length} {todayRows.length === 1 ? 'meeting' : 'meetings'} today ·{' '}
            {open.data?.counts.open ?? '–'} open
            {next ? ` · ${next.title} ${until(next.startsAt)}, will record` : ''}
          </p>
        </div>
      </div>
      <div className="grid-3" style={{ alignItems: 'stretch' }}>
        <Column
          title="Notes"
          sub="what was said, written up"
          foot={<Link to="/meetings">All {meetings.data?.length ?? ''} meetings →</Link>}
        >
          {meetings.isPending ? (
            <SkeletonRows rows={3} />
          ) : meetings.isError ? (
            <Broken what="Your notes" retry={() => void meetings.refetch()} />
          ) : recent.length === 0 && !live ? (
            <Empty
              lead={
                next
                  ? `Your first recorded meeting is ${until(next.startsAt)}.`
                  : 'Nothing recorded yet.'
              }
            >
              The note is written within two minutes of the call ending.
            </Empty>
          ) : (
            <div className="rows">
              {live ? (
                <div className="row" style={{ background: 'var(--sunk)' }}>
                  <span className="dot dot-live" />
                  <div className="row-main">
                    <span className="row-title">{live.title}</span>
                    <span className="mono">
                      {live.capture === 'RECORDING'
                        ? 'recording · the note follows the call'
                        : live.capture === 'WAITING_ROOM'
                          ? 'at the door — admit it'
                          : 'joining'}
                    </span>
                  </div>
                </div>
              ) : null}
              {recent.map((m) => (
                <NoteRow key={m.id} m={m} />
              ))}
            </div>
          )}
        </Column>

        <Column
          title="Open"
          sub="what people owe each other"
          foot={<Link to="/open">Both sides of the ledger →</Link>}
        >
          {open.isPending ? (
            <SkeletonRows rows={3} />
          ) : !open.data ? (
            <Broken what="Your ledger" retry={() => void open.refetch()} />
          ) : items.length === 0 ? (
            <Empty tone="ok" lead="Nothing outstanding." />
          ) : (
            <>
              <div
                className="figures"
                style={{ padding: '16px 20px', borderBottom: '1px solid var(--line)' }}
              >
                <div className="figure">
                  <span className="n">{mine.length}</span>
                  <span className="k">you owe</span>
                </div>
                <div className="figure">
                  <span className="n">{theirs.length}</span>
                  <span className="k">owed to you</span>
                </div>
                <div className="figure">
                  <span className={`n ${open.data.counts.overWeek ? 'amber' : ''}`}>
                    {open.data.counts.overWeek}
                  </span>
                  <span className="k">over a week</span>
                </div>
              </div>
              <div className="rows">
                {shown.map((i) => (
                  <OpenRow
                    key={i.id}
                    id={i.id}
                    text={i.text}
                    mine={i.direction === 'I_OWE'}
                    ownerEmail={i.ownerEmail}
                    ownerName={
                      i.ownerName ??
                      (i.ownerEmail ? (names.get(i.ownerEmail.toLowerCase()) ?? null) : null)
                    }
                    createdAt={i.createdAt}
                    grade={i.grade}
                    closed={i.status !== 'OPEN'}
                  />
                ))}
              </div>
            </>
          )}
        </Column>

        <Column
          title="Knowledge"
          sub="what stuck, and what changed"
          foot={<Link to="/knowledge">Everything we know →</Link>}
        >
          <KnowledgeColumn />
        </Column>
      </div>
    </div>
  );
}

/**
 * What stuck: what is waiting for you to confirm, then the newest of what we
 * know.
 *
 * ⚠ This column was a fixed "Nothing has stuck yet" from the day Knowledge
 * shipped until 2026-09-24 — it never asked the server anything, while the
 * Knowledge page held nine statements and two proposals. "why the knowledge at
 * the home page is constantly empty?" (Vladimir).
 */
function KnowledgeColumn(): ReactElement {
  const knowledge = useKnowledge();
  if (knowledge.isPending) return <SkeletonRows rows={3} />;
  if (knowledge.isError)
    return <Broken what="What we know" retry={() => void knowledge.refetch()} />;
  const { counts, proposed, current } = knowledge.data;
  if (counts.statements + counts.proposed === 0) {
    return (
      <Empty lead="Nothing has stuck yet.">
        Knowledge fills itself from your meetings. Nothing is proposed on a single mention, so the
        first statements usually appear after the second or third meeting on a topic.
      </Empty>
    );
  }
  const newest = [...current].sort(
    (a, b) => Date.parse(b.lastMentionedAt) - Date.parse(a.lastMentionedAt),
  );
  const rows = [
    ...proposed.slice(0, 2).map((e) => ({ e, waiting: true })),
    ...newest.map((e) => ({ e, waiting: false })),
  ].slice(0, 4);
  return (
    <>
      <div
        className="figures"
        style={{ padding: '16px 20px', borderBottom: '1px solid var(--line)' }}
      >
        <div className="figure">
          <span className="n">{counts.statements}</span>
          <span className="k">we know</span>
        </div>
        <div className="figure">
          <span className={`n ${counts.proposed ? 'amber' : ''}`}>{counts.proposed}</span>
          <span className="k">waiting for you</span>
        </div>
      </div>
      <div className="rows">
        {rows.map(({ e, waiting }) => (
          <Link key={e.id} to="/knowledge" className="row stacked" style={{ color: 'inherit' }}>
            <div className="row-main">
              <span className="row-title">{e.statement}</span>
              <span className="row-sub">
                <span className={`mono ${waiting ? 'amber' : ''}`}>
                  {waiting ? 'confirm?' : ago(e.lastMentionedAt)}
                </span>
                {e.lastSource ? <span className="truncate">{e.lastSource.title}</span> : null}
              </span>
            </div>
          </Link>
        ))}
      </div>
    </>
  );
}

function Column({
  title,
  sub,
  foot,
  children,
}: {
  title: string;
  sub: string;
  foot: ReactNode;
  children: ReactNode;
}): ReactElement {
  return (
    <section className="card column">
      <header className="card-head">
        <h2 className="t-heading">{title}</h2>
        <div className="t-secondary">{sub}</div>
      </header>
      <div className="grow" style={{ display: 'flex', flexDirection: 'column' }}>
        {children}
      </div>
      <div className="card-foot">{foot}</div>
    </section>
  );
}

function NoteRow({ m }: { m: MeetingSummary }): ReactElement {
  const age = Date.now() - Date.parse(m.startedAt);
  const when = isToday(m.startedAt)
    ? clock(m.startedAt)
    : dayKey(m.startedAt) === dayKey(new Date(Date.now() - 86_400_000).toISOString())
      ? 'Yesterday'
      : age < 6 * 86_400_000
        ? new Date(m.startedAt).toLocaleDateString('en-GB', { weekday: 'short' })
        : dateLabel(m.startedAt);
  const writing =
    !m.enhanced &&
    m.captureHealth === 'DONE' &&
    Date.now() - Date.parse(m.endedAt ?? m.startedAt) < 15 * 60_000;
  return (
    <Link to={`/m/${m.id}`} className="row" style={{ alignItems: 'flex-start' }}>
      <div className="row-main">
        <span className="row-title">
          <span className="mono" style={{ marginRight: 10 }}>
            {when}
          </span>
          {m.title}
        </span>
        {writing ? (
          <span className="mono">writing up · about a minute</span>
        ) : m.captureHealth === 'FAILED' ? (
          <span className="t-secondary red">not recorded — {whyNothing(m.captureReason).line}</span>
        ) : m.lead ? (
          <span className="t-secondary">{m.lead}</span>
        ) : (
          <span className="t-secondary">produced nothing — a record of the conversation</span>
        )}
        {m.enhanced ? (
          <span className="cluster" style={{ gap: 6 }}>
            <span className="pill">note</span>
            <span className="pill">transcript</span>
          </span>
        ) : null}
      </div>
    </Link>
  );
}

function OpenRow(props: {
  id: string;
  text: string;
  mine: boolean;
  ownerEmail: string | null;
  ownerName: string | null;
  createdAt: string;
  grade: 'HEARD' | 'ASSUMED' | 'WRITTEN' | 'CONFIRMED';
  /** Closed a moment ago, here: still in its slot, crossed out, with Undo. */
  closed: boolean;
}): ReactElement {
  const ledger = useLedgerActions();
  const { show } = useNotices();
  const ageing = Date.now() - Date.parse(props.createdAt) > 7 * 86_400_000;
  return (
    <div className={`row stacked ${props.closed ? 'closed-here' : ''}`}>
      <div className="row-main">
        <span className="row-title">
          {props.mine || !props.ownerEmail
            ? ''
            : `${firstName(props.ownerName, props.ownerEmail)}: `}
          {props.text}
        </span>
        <span className="row-sub">
          <span className={`mono ${ageing ? 'amber' : ''}`}>{ago(props.createdAt)}</span>
          <Grade value={props.grade} />
        </span>
      </div>
      {props.closed ? (
        <Button size="sm" onClick={() => ledger.reopen.mutate(props.id)}>
          Undo
        </Button>
      ) : (
        <Button
          size="sm"
          onClick={() =>
            ledger.close.mutate(
              { id: props.id, body: { status: 'DONE' } },
              {
                onSuccess: () =>
                  show({
                    tone: 'success',
                    text: 'Marked done.',
                    action: { label: 'Undo', onSelect: () => ledger.reopen.mutate(props.id) },
                  }),
              },
            )
          }
        >
          {props.mine ? 'Done' : 'Got it'}
        </Button>
      )}
    </div>
  );
}
